<template>
    <div class="energy-color-palette-compact">
        <!-- 컬러 미리보기 버튼 -->
        <div class="color-preview-button" @click="toggleDropdown" ref="button">
            <div class="color-preview-box" :style="{ backgroundColor: selectedColor }">
                <span class="color-code">{{ selectedColor }}</span>
            </div>
            <i class="fa fa-chevron-down dropdown-arrow" :class="{ rotated: isDropdownOpen }"></i>
        </div>

        <!-- 드롭다운 팝업 -->
        <div v-if="isDropdownOpen" class="color-dropdown" ref="dropdown">
            <!-- 에너지 타입 선택 탭 -->
            <div class="energy-type-tabs">
                <button
                    v-for="(type, key) in dynamicEnergyTypes"
                    :key="key"
                    class="energy-tab"
                    :class="{ active: selectedEnergyType === key }"
                    @click="selectEnergyType(key)"
                    :title="type.label"
                >
                    <span class="tab-label">{{ type.label }}</span>
                </button>
            </div>

            <!-- 색상 팔레트 그리드 -->
            <div class="color-palette-container">
                <div class="palette-grid">
                    <div
                        v-for="(color, index) in currentColorPalette"
                        :key="index"
                        class="color-item"
                        :class="{ selected: selectedColor === color }"
                        @click="selectColor(color)"
                        :title="`${color} 선택`"
                    >
                        <div class="color-label">{{ getColorLabel(color) }}</div>
                        <div class="color-preview-bottom">
                            <div class="color-preview" :style="{ backgroundColor: color }">
                                <i v-if="selectedColor === color" class="fa fa-check"></i>
                            </div>
                            <div class="color-hex">{{ color }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 자유 선택 색상 -->
            <div class="custom-color-section">
                <div class="custom-color-header">
                    <span>자유 선택</span>
                </div>
                <div class="custom-color-input">
                    <input
                        type="color"
                        class="color-picker"
                        v-model="customColor"
                        @change="selectCustomColor"
                        title="색상 선택"
                    />
                    <div class="color-preview" :style="{ backgroundColor: customColor }">
                        <span class="color-code">{{ customColor }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
export default {
    name: "EnergyColorPalette",
    props: {
        value: {
            type: String,
          
        },
        energyType: {
            type: String,
            default: "renewable",
        },
        /**
         * 설비 유형 코드 페이지에 붙이는 과정에서 필요한 옵션 정의
         * 1. 컬러 미리보기 버튼 패딩
         * 2. 드롭다운 표출 위치
         * 3. 드롭다운 disabled 옵션
         */
        colorPreviewButtonPadding: {
            type: String,
            default: "4px 6px",
        },
        dropdownPosition: {
            type: String,
            default: "0",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedEnergyType: this.energyType || "",
            selectedColor: this.value,
            customColor: this.value || "#4CAF50",
            isDropdownOpen: false,
        };
    },
    computed: {
        ...mapState({
            widgetColor: (state) => state.commonCodes.widgetColor,
            commonCodes: (state) => state.commonCodes,
        }),
        // widgetColor 데이터에서 에너지 타입과 색상 팔레트 동적 생성
        dynamicEnergyTypes() {
            if (!this.widgetColor || !Array.isArray(this.widgetColor)) return {};
            const types = {};
            this.widgetColor.forEach((item, index) => {
                if (item && item.text && item.value) {
                    types[item.value] = { label: item.text, index };
                }
            });
            return types;
        },

        // widgetColor의 value를 사용해서 commonCodes에서 backgroundColor 추출
        dynamicColorPalettes() {
            if (!this.widgetColor || !Array.isArray(this.widgetColor) || !this.commonCodes) {
                return {};
            }
            const palettes = {};
            this.widgetColor.forEach((wc) => {
                if (!wc || !wc.value) return;
                const codeData = this.findCodeDataByValue(wc.value);
                if (Array.isArray(codeData) && codeData.length > 0) {
                    const colorItems = codeData
                        .map((itm) => {
                            const raw = itm.backgroundColor;
                            if (!raw) return null;
                            const safe = String(raw).startsWith("#") ? String(raw) : `#${String(raw)}`;
                            return { color: safe, label: itm.label || "", fontColor: itm.color || "" };
                        })
                        .filter(Boolean);

                    if (colorItems.length > 0) {
                        palettes[wc.value] = colorItems;
                    }
                }
            });
            return palettes;
        },

        currentColorPalette() {
            const palettes = this.dynamicColorPalettes;
            const typeKeys = Object.keys(palettes);
            // selectedEnergyType이 없거나 유효하지 않으면 첫 번째 키로 보정
            const effectiveType =
                this.selectedEnergyType && palettes[this.selectedEnergyType] ? this.selectedEnergyType : typeKeys[0];

            const items = (palettes[effectiveType] || []).map((i) => i.color);
            return items;
        },
    },
    watch: {
        value: {
            handler(newVal) {
                this.selectedColor = newVal;
                if (newVal) this.customColor = newVal;
                this.findAndSelectColor(newVal);
            },
            immediate: true,
        },
        selectedColor(newVal) {
            this.$emit("input", newVal);
        },
        // widgetColor가 로드되면 탭/팔레트 교정
        widgetColor: {
            handler() {
                this.$nextTick(() => {
                    this.ensureValidEnergyTypeAndInit();
                });
            },
            immediate: true,
        },
        // commonCodes가 늦게 올 수도 있으니 보정
        commonCodes: {
            handler() {
                this.$nextTick(() => {
                    this.ensureValidEnergyTypeAndInit();
                });
            },
            immediate: true,
        },
        // currentColorPalette 변화를 감지하여 에너지 타입 보정
        currentColorPalette: {
            handler(newPalette, oldPalette) {
                if (oldPalette && newPalette && newPalette.length > 0) {
                    const palettes = this.dynamicColorPalettes;
                    const typeKeys = Object.keys(palettes);
                    const effectiveType = typeKeys[0];

                    if (
                        effectiveType &&
                        this.selectedEnergyType !== effectiveType &&
                        !palettes[this.selectedEnergyType]
                    ) {
                        this.$nextTick(() => {
                            this.selectedEnergyType = effectiveType;
                        });
                    }
                }
            },
        },
    },
    mounted() {
        if (this.value) {
            this.selectedColor = this.value;
        }
        this.$nextTick(() => {
            this.ensureValidEnergyTypeAndInit();
        });
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        toggleDropdown() {
            if (this.disabled) return;
            this.isDropdownOpen = !this.isDropdownOpen;
        },
        handleClickOutside(event) {
            if (
                this.$refs.button &&
                !this.$refs.button.contains(event.target) &&
                this.$refs.dropdown &&
                !this.$refs.dropdown.contains(event.target)
            ) {
                this.isDropdownOpen = false;
            }
        },
        selectEnergyType(type) {
            this.selectedEnergyType = type;
        },
        selectColor(color, closeDropdown = true) {
            this.selectedColor = color;
            if (closeDropdown) this.isDropdownOpen = false;
        },
        // 자유선택 색상 피커에서 색상 선택
        selectCustomColor() {
            this.selectColor(this.customColor);
        },

        ensureValidEnergyTypeAndInit() {
            const types = this.dynamicEnergyTypes;
            const palettes = this.dynamicColorPalettes;
            const typeKeys = Object.keys(types).length ? Object.keys(types) : Object.keys(palettes);

            if (!typeKeys.length) return;

            // 현재 선택 타입이 유효하지 않으면 첫 번째로 보정
            if (!this.selectedEnergyType || !(this.selectedEnergyType in palettes)) {
                this.selectedEnergyType = typeKeys[0];
            }

            // value가 있으면 해당 색상으로 탭/선택 보정
            if (this.value) {
                this.findAndSelectColor(this.value);
            } else {
                // value 없으면 첫 팔레트의 첫 색상 선택
                const firstList = palettes[this.selectedEnergyType] || [];
                if (firstList.length && (!this.selectedColor || !this.isColorInPalettes(this.selectedColor))) {
                    this.selectColor(firstList[0].color, false);
                }
            }
        },

        // widgetColor의 value로 commonCodes에서 해당 코드 데이터 찾기
        findCodeDataByValue(widgetValue) {
            if (!this.commonCodes || !this.commonCodes[widgetValue]) return null;
            const colorCode = this.commonCodes[widgetValue];
            if (!Array.isArray(colorCode)) return null;
            return colorCode.map((item) => ({
                value: item.value,
                label: item.text,
                backgroundColor: item.options?.backgroundColor,
                color: item.options?.color,
            }));
        },

        // 전달받은 색상을 프리셋에서 찾아서 해당 타입과 색상 선택
        findAndSelectColor(targetColor) {
            if (!targetColor) return;
            const palettes = this.dynamicColorPalettes;
            let foundType = null;

            for (const [energyType, colors] of Object.entries(palettes)) {
                if (colors.some((ci) => ci.color.toLowerCase() === String(targetColor).toLowerCase())) {
                    foundType = energyType;
                    break;
                }
            }

            if (foundType) {
                if (this.selectedEnergyType !== foundType) this.selectedEnergyType = foundType;
                this.selectedColor = targetColor;
                return;
            }

            // 프리셋에서 찾지 못한 경우: 첫 번째 탭 선택 후 자유선택
            const typeKeys = Object.keys(palettes);
            if (typeKeys.length > 0) {
                this.selectedEnergyType = typeKeys[0];
            }
            this.customColor = targetColor;
            this.selectedColor = targetColor;
        },

        // 색상 팔레트 전체에 해당 색상이 존재하는지
        isColorInPalettes(color) {
            const palettes = this.dynamicColorPalettes;
            for (const arr of Object.values(palettes)) {
                if (arr.some((ci) => ci.color.toLowerCase() === String(color).toLowerCase())) return true;
            }
            return false;
        },

        // 색상에 해당하는 라벨 가져오기
        getColorLabel(color) {
            const palettes = this.dynamicColorPalettes;
            for (const colorItems of Object.values(palettes)) {
                const found = colorItems.find((item) => item.color.toLowerCase() === String(color).toLowerCase());
                if (found) return found.label || "";
            }
            return "";
        },
    },
};
</script>

<style scoped>
.energy-color-palette-compact {
    position: relative;
    display: inline-block;
    width: 100%;
}

/* 컬러 미리보기 버튼 */
.color-preview-button {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: v-bind(colorPreviewButtonPadding);
    /* padding: 8px 12px; */
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 90px;
    /* max-width: 100px; */
    height: 34px;
}

.color-preview-button:hover {
    border-color: #007bff;
    background: #f8f9ff;
}

.color-preview-button:active {
    background: #f0f4ff;
}

.color-preview-box {
    flex: 1;
    /* height: 18px; */
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    /* min-width: 45px; */
}

.color-code {
    color: white;
    /* font-size: 11px; */
    font-weight: 600;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
    font-family: "Roboto Mono", "Noto Sans KR", monospace;
}

.dropdown-arrow {
    font-size: 9px;
    color: #adb5bd;
    transition: all 0.2s ease;
}

.color-preview-button:hover .dropdown-arrow {
    color: #007bff;
}

.dropdown-arrow.rotated {
    transform: rotate(180deg);
}

/* 드롭다운 팝업 */
.color-dropdown {
    position: absolute;
    top: 100%;
    left: v-bind(dropdownPosition);
    /* min-width: 320px;
    max-width: 480px; */
    width: 400px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    margin-top: 4px;
    overflow: hidden;
}

/* 에너지 타입 탭 */
.energy-type-tabs {
    display: flex;
    flex-wrap: wrap;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
}

.energy-tab {
    flex: 1;
    min-width: 60px;
    padding: 10px 8px;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    color: #6c757d;
    font-size: 12px;
    font-weight: 500;
    box-sizing: border-box;
}

.energy-tab:hover {
    background: #e9ecef;
    color: #495057;
}

.energy-tab.active {
    background: white;
    color: #007bff;
    border-bottom-color: #007bff;
    outline: none;
}

.energy-tab:focus {
    outline: none;
}

.tab-label {
    font-weight: 500;
}

/* 색상 팔레트 */
.color-palette-container {
    padding: 16px;
    max-height: 500px;
    overflow-y: auto;
}

.palette-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;

    /* overflow-y: auto; */
}

.color-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 4px;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    /* min-width: 44px; */
    height: auto;
    justify-content: space-between;
    min-height: 80px;
}

.color-preview {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 2px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.color-item:hover .color-preview {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.color-item.selected .color-preview {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.color-label {
    font-size: 10px;
    color: #495057;
    text-align: center;
    line-height: 1.3;
    max-width: 100%;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    font-weight: 600;
    padding: 3px 4px;
    border-radius: 3px;
    min-height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    hyphens: auto;
    text-align-last: center;
}

.color-item i {
    color: white;
    font-size: 10px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.color-hex {
    font-size: 9px;
    color: #6c757d;
    font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
    font-weight: 500;
    text-align: center;
    padding: 2px 4px;
    background: #f8f9fa;
    border-radius: 3px;
    border: 1px solid #e9ecef;
    margin-top: 2px;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    word-break: break-all;
    line-height: 1.2;
}

/* 자유 선택 색상 */
.custom-color-section {
    padding: 0 16px 16px;
    border-top: 1px solid #e9ecef;
}

.custom-color-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    margin-top: 5px;
    color: #495057;
    font-weight: 600;
    font-size: 12px;
}

.custom-color-input {
    display: flex;
    align-items: center;
    gap: 10px;
}

.color-picker {
    width: 40px;
    height: 28px;
    border: 2px solid #dee2e6;
    border-radius: 6px;
    cursor: pointer;
    padding: 0;
    background: none;
    transition: border-color 0.2s ease;
}

.color-picker:hover {
    border-color: #007bff;
}

.color-picker:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
.color-preview-bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    height: 100%;
    gap: 4px;
}

/* 이 아래 .color-preview는 커스텀 영역 내부 것만 영향 주도록 더 구체화 */
.custom-color-input .color-preview {
    flex: 1;
    height: 28px;
    border-radius: 6px;
    border: 2px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

/* 반응형 */
@media (max-width: 480px) {
    .color-dropdown {
        min-width: 280px;
        max-width: 320px;
        width: auto;
    }

    .palette-grid {
        grid-template-columns: repeat(5, 1fr);
        gap: 6px;
    }

    .color-item {
        min-width: 36px;
        min-height: 70px;
    }

    .color-preview {
        width: 28px;
        height: 28px;
    }

    .energy-tab {
        padding: 8px 6px;
        font-size: 11px;
    }
}
</style>
